import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/** Enhances the native product form, gallery and fit guide without duplicating cart logic. */
class DeluluProduct extends Component {
  #controller;
  #observer;
  #buttonObserver;
  #opener;
  #busy = false;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const section = this.closest('.shopify-section');
    section?.addEventListener(ThemeEvents.variantSelected, () => {
      this.#busy = true;
      this.#sync();
    }, { signal });
    section?.addEventListener(ThemeEvents.variantUpdate, () => {
      this.#busy = false;
      queueMicrotask(() => this.isConnected && this.#sync());
    }, { signal });
    this.addEventListener('click', this.#click, { signal });
    this.querySelector('[data-model-dialog]')?.addEventListener('close', () => this.#opener?.focus(), { signal });
    const button = this.querySelector('product-form-component [ref="addToCartButton"]');
    if (button) {
      this.#observer = new IntersectionObserver(([entry]) => {
        const bar = this.querySelector('[data-sticky-buy]');
        if (bar) bar.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
      }, { threshold: 0 });
      this.#observer.observe(button);
      this.#buttonObserver = new MutationObserver(() => this.#sync());
      this.#buttonObserver.observe(button, { attributes: true, childList: true, subtree: true });
    }
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#buttonObserver?.disconnect();
  }

  #sync() {
    const main = this.querySelector('product-form-component [ref="addToCartButton"]');
    const sticky = this.querySelector('[data-sticky-submit]');
    if (!main || !sticky) return;
    sticky.disabled = main.disabled || this.#busy;
    sticky.textContent = main.querySelector('.add-to-cart-text__content')?.textContent.trim() || this.dataset.addLabel;
    const price = this.querySelector('.product-details product-price .price');
    const stickyPrice = this.querySelector('[data-sticky-price]');
    if (price && stickyPrice) stickyPrice.textContent = price.textContent.trim();
    const options = [...this.querySelectorAll('.product-details variant-picker fieldset input:checked')].map(input => input.value);
    const selection = this.querySelector('[data-sticky-selection]');
    if (selection) selection.textContent = options.join(' / ');
  }

  #click = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('[data-sticky-submit]')) {
      const form = this.querySelector('product-form-component form');
      const button = form?.querySelector('[ref="addToCartButton"]');
      if (!form || !button || button.disabled || this.#busy) return;
      // Bundles and custom products can add required fields to this native form.
      const invalid = form.querySelector(':invalid');
      if (invalid) {
        invalid.scrollIntoView({ block: 'center', behavior: this.#motion() });
        invalid.focus();
      }
      form.requestSubmit(button);
    }
    const opener = event.target.closest('[data-model-open]');
    if (opener) {
      const dialog = this.querySelector('[data-model-dialog]');
      const image = dialog?.querySelector('img');
      if (dialog && image) {
        this.#opener = opener;
        image.src = opener.dataset.modelOpen;
        image.alt = opener.querySelector('img')?.alt || '';
        dialog.showModal();
      }
    }
    const dialog = this.querySelector('[data-model-dialog]');
    if (event.target.closest('[data-model-close]') || event.target === dialog) dialog?.close();
  };

  #motion() {
    return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
  }
}

if (!customElements.get('delulu-product')) customElements.define('delulu-product', DeluluProduct);
